import { useSyncExternalStore } from 'react'

/**
 * Tenant locale — the ONE seam for number/date formatting across the app.
 *
 * The tenant declares its locale (BCP-47) in the backend (monad.tenants.locale).
 * At app boot, App.tsx fetches GET /tenants/{slug} and calls setLocale(locale).
 * All formatters read this module; nothing else carries locale intent.
 *
 * Default 'en-US' is the safe fallback for dev/test environments where
 * getTenant hasn't been called yet.
 */

let _locale = 'en-US'
let _localeExplicit = false

/** Set the tenant locale once at app boot. Idempotent. */
export function setLocale(locale: string): void {
  _locale = locale
  _localeExplicit = true
}

/** Read the active tenant locale. Call this inside formatters, not components. */
export function getLocale(): string {
  return _locale
}

// ---------------------------------------------------------------------------
// THE date/time formatter (SEN-439 X7: "why not unify date formats"). Every date a user reads is formatted
// HERE, in a few named styles, in the tenant's locale and zone — never an ad-hoc toLocaleDateString, a
// date-fns format() or a private Intl.DateTimeFormat (the __guards__/date-formats test fails them).
//
//   date        21 Jan 2026            dateTime   21 Jan 2026, 18:41       time   18:41
//   shortDay    21 Jan                 monthYear  Jan 2026                 weekday  Wed
//   period(a,b) 1 Sep – 22 Oct 2025    relative    3 days ago / in 2 hours
//
// Until the tenant declares its own, the locale is en-GB (day month year, 24-hour) and the zone is
// Europe/Copenhagen: the product's market, never the browser's setting.
// ---------------------------------------------------------------------------

export type DateStyle = 'date' | 'dateTime' | 'time' | 'shortDay' | 'monthYear' | 'weekday' | 'monthLong'
export type DateInput = string | number | Date | null | undefined

/** The named styles' Intl options (also served to artifact pages, so their dates read like the app's). */
export const DATE_STYLE_OPTIONS: Record<DateStyle, Intl.DateTimeFormatOptions> = {
  date: { day: 'numeric', month: 'short', year: 'numeric' },
  dateTime: { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' },
  time: { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' },
  shortDay: { day: 'numeric', month: 'short' },
  monthYear: { month: 'short', year: 'numeric' },
  monthLong: { month: 'long' },
  weekday: { weekday: 'short' },
}
const STYLE_OPTS = DATE_STYLE_OPTIONS

/** The locale dates are shown in: the tenant's once declared, en-GB before that. */
export function getDateLocale(): string {
  return _localeExplicit ? _locale : 'en-GB'
}

/** The zone dates are shown in: the tenant's once declared, Europe/Copenhagen before that. */
export function getDateZone(): string {
  return _zoneExplicit ? _zone : 'Europe/Copenhagen'
}

export function toDate(value: DateInput): Date | null {
  if (value === null || value === undefined || value === '') return null
  // a bare "2026-09-01" is a calendar day, not an instant: keep it on that day in every zone
  const d = value instanceof Date ? value : typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value) ? new Date(`${value}T12:00:00Z`) : new Date(value)
  return Number.isNaN(d.getTime()) ? null : d
}

function isCalendarDay(value: DateInput): boolean {
  return typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)
}

const _fmtCache = new Map<string, Intl.DateTimeFormat>()
function fmt(opts: Intl.DateTimeFormatOptions, zone: string, locale: string = getDateLocale()): Intl.DateTimeFormat {
  const key = `${locale}|${zone}|${JSON.stringify(opts)}`
  let f = _fmtCache.get(key)
  if (!f) { f = new Intl.DateTimeFormat(locale, { ...opts, timeZone: zone }); _fmtCache.set(key, f) }
  return f
}

/** A call may pin the zone or the locale (a view that already resolved them for its own window). */
export interface DateOverride { zone?: string; locale?: string }

/** A date or moment in a named style (or, for a rare special case, explicit Intl options). "—" when there is none. */
export function formatDate(value: DateInput, style: DateStyle | Intl.DateTimeFormatOptions = 'date', over: DateOverride = {}): string {
  const d = toDate(value)
  if (!d) return '—'
  const opts = typeof style === 'string' ? STYLE_OPTS[style] : style
  // a calendar day (no time) never shifts with the zone
  const zone = isCalendarDay(value) ? 'UTC' : over.zone ?? getDateZone()
  return fmt(opts, zone, over.locale).format(d)
}

export const formatDateTime = (value: DateInput): string => formatDate(value, 'dateTime')
export const formatTime = (value: DateInput): string => formatDate(value, 'time')
export const formatShortDay = (value: DateInput): string => formatDate(value, 'shortDay')

/** "1 Sep – 22 Oct 2025": the year once when both ends share it. "—" when either end is missing. */
export function formatPeriod(start: DateInput, end: DateInput, over: DateOverride = {}): string {
  const a = toDate(start), b = toDate(end)
  if (!a || !b) return '—'
  const zoneA = isCalendarDay(start) ? 'UTC' : over.zone ?? getDateZone()
  const zoneB = isCalendarDay(end) ? 'UTC' : over.zone ?? getDateZone()
  const year = (d: Date, z: string) => fmt({ year: 'numeric' }, z, 'en').format(d)
  const sameYear = year(a, zoneA) === year(b, zoneB)
  const left = fmt(sameYear ? STYLE_OPTS.shortDay : STYLE_OPTS.date, zoneA, over.locale).format(a)
  return `${left} – ${fmt(STYLE_OPTS.date, zoneB, over.locale).format(b)}`
}

/** "3 days ago" / "in 2 hours" (Intl.RelativeTimeFormat); falls back to the date beyond a month. */
export function formatRelative(value: DateInput, now: Date = new Date()): string {
  const d = toDate(value)
  if (!d) return '—'
  const secs = Math.round((d.getTime() - now.getTime()) / 1000)
  const abs = Math.abs(secs)
  const rtf = new Intl.RelativeTimeFormat(getDateLocale(), { numeric: 'auto' })
  if (abs < 60) return rtf.format(secs, 'second')
  if (abs < 3600) return rtf.format(Math.round(secs / 60), 'minute')
  if (abs < 86400) return rtf.format(Math.round(secs / 3600), 'hour')
  if (abs < 86400 * 30) return rtf.format(Math.round(secs / 86400), 'day')
  return formatDate(d)
}

/** "18:41" for a moment of today, "21 Jan" before that: compact lists (the chat's thread list). */
export function formatListStamp(value: DateInput, now: Date = new Date()): string {
  const d = toDate(value)
  if (!d) return ''
  return formatDate(d, 'shortDay') === formatDate(now, 'shortDay') ? formatTime(d) : formatShortDay(d)
}

/** Month names for a picker ("January"…), index 0-11. */
export function monthName(index: number): string {
  return fmt(STYLE_OPTS.monthLong, 'UTC').format(new Date(Date.UTC(2000, index, 15)))
}

// ---------------------------------------------------------------------------
// Tenant timezone — the SAME seam (SEN-1761). The tenant declares its IANA
// zone in settings (monad settings-as-records `timezone` cell); App boot
// calls setTenantZone alongside setLocale. Preset period windows and bucket
// axis labels read THIS — a Copenhagen "previous month" is the Copenhagen
// calendar month (743/745 hours across DST), never the UTC one. The server
// twin is monad/period_binding.py::resolve_period(tz=...).
// ---------------------------------------------------------------------------

let _zone = 'UTC'
let _zoneExplicit = false
// PR269 period review: the zone is a SUBSCRIBABLE value, not a bare module
// variable — a mounted, period-bound view must recompute its window when the
// tenant clock ARRIVES (the mount-race: dashboard data can win the race
// against the settings fetch) or CHANGES (an authorized settings edit).
const _zoneSubs = new Set<() => void>()

/** Set the tenant IANA zone (app boot + settings refresh). Unknown names
 *  degrade to UTC (annotate-not-block — a mistyped zone must not take the
 *  app down), mirroring the server's _zone(). Notifies subscribers only on
 *  an actual change. */
export function setTenantZone(zone: string | null | undefined): void {
  let next = 'UTC'
  if (zone) {
    try {
      new Intl.DateTimeFormat('en', { timeZone: zone })
      next = zone
    } catch {
      next = 'UTC'
    }
  }
  _zoneExplicit = next !== 'UTC' || zone === 'UTC'
  if (next !== _zone) {
    _zone = next
    for (const cb of _zoneSubs) cb()
  }
}

/** Read the active tenant zone. Call inside resolvers/formatters, not components. */
export function getTenantZone(): string {
  return _zone
}

/** Subscribe to zone changes (useTenantZone's store contract). */
export function subscribeTenantZone(cb: () => void): () => void {
  _zoneSubs.add(cb)
  return () => { _zoneSubs.delete(cb) }
}

/** React view of the tenant zone: a component (or memo) depending on this
 *  value RECOMPUTES when the clock arrives or changes — "a hook that only
 *  changes the global variable without memo dependencies is insufficient"
 *  (PR269 period review). */
export function useTenantZone(): string {
  return useSyncExternalStore(subscribeTenantZone, getTenantZone, getTenantZone)
}

// ---------------------------------------------------------------------------
// Tenant fiscal year — the SAME seam (SEN-433 T7). The tenant's
// `fiscal_year_start_month` (TenantSettings, 1-12) anchors the `fytd` preset;
// the host sets it from the tenant read it already makes, next to the zone
// (Poiesis App boot; Sentinel's services/nomos/tz.ts). `null` = not known yet:
// PeriodChip does not offer `fytd` until it is, and the resolver reads a
// January fiscal year meanwhile — the server's own default.
// ---------------------------------------------------------------------------

let _fiscalMonth: number | null = null
const _fiscalSubs = new Set<() => void>()

/** Set the tenant's fiscal start month (1-12). Anything else is "not known"
 *  (null). Notifies subscribers only on an actual change. */
export function setTenantFiscalMonth(month: unknown): void {
  const next = typeof month === 'number' && Number.isInteger(month) && month >= 1 && month <= 12 ? month : null
  if (next !== _fiscalMonth) {
    _fiscalMonth = next
    for (const cb of _fiscalSubs) cb()
  }
}

/** The tenant's fiscal start month, or null while it is not known. */
export function getTenantFiscalMonth(): number | null {
  return _fiscalMonth
}

/** Subscribe to fiscal-month changes (useTenantFiscalMonth's store contract). */
export function subscribeTenantFiscalMonth(cb: () => void): () => void {
  _fiscalSubs.add(cb)
  return () => { _fiscalSubs.delete(cb) }
}

/** React view of the fiscal month: a period memo depending on it recomputes
 *  when it arrives or changes, as useTenantZone. */
export function useTenantFiscalMonth(): number | null {
  return useSyncExternalStore(subscribeTenantFiscalMonth, getTenantFiscalMonth, getTenantFiscalMonth)
}

// ---------------------------------------------------------------------------
// The count unit — the SAME seam. How a count of things is written in the unit slot ("#") is the units
// registry's display_symbol, served on the tenant read (`display.count_unit`); the host sets it next to
// the zone and the fiscal month. No client holds the literal. Empty until known.
// ---------------------------------------------------------------------------

let _countUnit = ''
const _countUnitSubs = new Set<() => void>()

/** Set the served count unit symbol (anything but a non-empty string is "not known": no unit shown). */
export function setCountUnit(symbol: unknown): void {
  const next = typeof symbol === 'string' ? symbol : ''
  if (next !== _countUnit) {
    _countUnit = next
    for (const cb of _countUnitSubs) cb()
  }
}

export function getCountUnit(): string {
  return _countUnit
}

/** React view of the count unit: a card re-renders when it arrives. */
export function useCountUnit(): string {
  return useSyncExternalStore((cb) => { _countUnitSubs.add(cb); return () => { _countUnitSubs.delete(cb) } }, getCountUnit, getCountUnit)
}

/** Minutes EAST of UTC the zone is at `at` (Copenhagen: +60 winter, +120 summer). */
export function tzOffsetMinutes(zone: string, at: Date): number {
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone: zone, hour12: false,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  })
  const p: Record<string, number> = {}
  for (const part of dtf.formatToParts(at)) {
    if (part.type !== 'literal') p[part.type] = Number(part.value)
  }
  const asUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour === 24 ? 0 : p.hour, p.minute, p.second)
  return Math.round((asUtc - at.getTime()) / 60_000)
}

/** The tenant-zone calendar date of `now` (the server's `now.astimezone(z).date()`). */
export function todayInZone(zone: string, now: Date): { y: number; m: number; d: number } {
  const dtf = new Intl.DateTimeFormat('en-US', { timeZone: zone, year: 'numeric', month: '2-digit', day: '2-digit' })
  const p: Record<string, number> = {}
  for (const part of dtf.formatToParts(now)) {
    if (part.type !== 'literal') p[part.type] = Number(part.value)
  }
  return { y: p.year, m: p.month - 1, d: p.day }
}

/** Local midnight of the zone's calendar day (y, m 0-based, d) as a UTC
 *  instant — the server's `_midnight`. Two-pass offset correction handles a
 *  DST change between the naive guess and the true instant. */
export function zonedMidnightUtc(zone: string, y: number, m: number, d: number): Date {
  let ts = Date.UTC(y, m, d)
  for (let i = 0; i < 2; i++) {
    ts = Date.UTC(y, m, d) - tzOffsetMinutes(zone, new Date(ts)) * 60_000
  }
  return new Date(ts)
}
